import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import styles from './Items.module.scss'
import useMenuItems from '../VNItems/hooks/useMenuItems'
import VNMenuItem from '../VNItems/components/VNMenuItem'
import { FixedSizeList as List } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { CircularProgress } from '@material-ui/core'
import { areEqual } from '../VNUtil/Memoization'
import { RESOURCE_RESTRAINED_HARDWARE } from '../VNMode/Utils'
import { bridge_getDeviceModel } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import VNNoItemsFound from '../VNCategories/components/VNNoItemsFound'
import { getBarcodeRestartState, getBarcodeScanningSettings } from '../selectors/config'
import { registerScannerHandlers, startScanner, stopScanner, unRegisterScannerHandlers } from '../kiosk/utils/scanner'
import { useDispatch, useSelector } from 'react-redux'
import { useBarcodeScanner } from '../hooks/useBarcodeScanner'
import { restartBarcodeScanner } from '../actions/config'

const MIN_ITEM_COUNT_MOBILE_THRESHOLD = 7
const TILE_PERCENT = 12.76 + 1.25
const TILE_PERCENT_MOBILE = 22.222 + 2.2
const LIST_BOTTOM_PADDING_MOBILE = '20rem'
const LIST_BOTTOM_PADDING = '3rem'
const OVERSCAN_COUNT = {
  RESTRAINED: 6,
  THRESHOLD: 36,
  THRESHOLD_MOBILE: 18,
  DEFAULT: 18,
}
const COLUMN_COUNT = {
  MOBILE: 2,
  TABLET: 6,
  DEFAULT: 5,
}

/**
 * Menu Items List component
 * Virutalized List of menu items
 *
 * @returns React.Element
 */
const Items = () => {
  const { getItems } = useMenuItems()
  const items = getItems()
  const barcodeScanSettings = useSelector(getBarcodeScanningSettings)
  const barcodeRestart = useSelector(getBarcodeRestartState)
  const listRef = useRef()
  const dispatch = useDispatch()

  // @todo [T39636] - Refactor to re-integrate refs in useCartItem hook
  const throttlingRef = useRef({})
  const isAddingToCartRef = useRef(new Set()) // checks if in middle of favoring an item
  const throttlingActiveRef = useRef({}) // checks if in middle of throttle timer
  const hasMinItemCount = useMemo(() => items?.length >= MIN_ITEM_COUNT_MOBILE_THRESHOLD, [items])

  const { addScannedItem } = useBarcodeScanner({
    throttlingRef,
    throttlingActiveRef,
  })

  useEffect(() => {
    if (barcodeScanSettings.barcodeScanningEnabled || barcodeRestart) {
      startScanner()
      registerScannerHandlers(
        (data) => addScannedItem(data),
        (err) => {},
      )
      dispatch(restartBarcodeScanner(false))
    }

    // cleanup function at dismount
    return () => {
      if (barcodeScanSettings.barcodeScanningEnabled) {
        stopScanner()
        unRegisterScannerHandlers()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodeRestart])

  const classNames = cn(styles.items, styles.favoritesEnabled, {
    [styles.hasMinItemCount]: hasMinItemCount,
  })
  const hasCartItems = useMemo(() => items?.some((item) => item?.cartQuantity > 0), [items])
  const isResourceRestrained = useMemo(() => RESOURCE_RESTRAINED_HARDWARE?.includes(bridge_getDeviceModel()), [])
  const isMobile = useMemo(() => window.matchMedia('(max-width: 768px)').matches, [])
  const isLargeTablet = useMemo(() => window.matchMedia('(min-width: 1080px)').matches, [])
  const overscanCount = useMemo(() => {
    if (!isResourceRestrained) {
      return OVERSCAN_COUNT.DEFAULT
    }

    if (isMobile) {
      return items?.length < OVERSCAN_COUNT.THRESHOLD_MOBILE ? OVERSCAN_COUNT.RESTRAINED : 0
    }

    return items?.length < OVERSCAN_COUNT.THRESHOLD ? OVERSCAN_COUNT.RESTRAINED : 0
  }, [isMobile, isResourceRestrained, items?.length])
  const columnsCount = useMemo(() => {
    switch (true) {
      case isMobile:
        return COLUMN_COUNT.MOBILE
      case isLargeTablet:
        return COLUMN_COUNT.TABLET
      default:
        return COLUMN_COUNT.DEFAULT
    }
  }, [isMobile, isLargeTablet])
  const itemsCount = useMemo(() => Math.ceil(items.length / columnsCount), [columnsCount, items?.length])

  /**
   * Calculate the row height to mimic the same behavior as CSS rules
   * for the row height in "react-window" List component to work
   *
   * NOTE: tile height is based off of the window width in the CSS
   */
  const tileHeight = useMemo(() => (window.innerWidth * (isMobile ? TILE_PERCENT_MOBILE : TILE_PERCENT)) / 100, [isMobile])

  const tileRenderer = useCallback(
    ({ index, isScrolling }) => {
      if (isScrolling) {
        if (isResourceRestrained) {
          return <div key={index} className={cn(styles.itemContainer, styles.loader)} />
        }

        return (
          <div key={index} className={cn(styles.itemContainer, styles.loader)}>
            <CircularProgress size="1em" color="inherit" />
          </div>
        )
      }
      const item = items[index]
      if (!item) return null

      return (
        <div className={styles.itemContainer} key={index} data-id={item.itemCategoryUuid}>
          <VNMenuItem
            data={item}
            throttlingRef={throttlingRef}
            throttlingActiveRef={throttlingActiveRef}
            isAddingToCartRef={isAddingToCartRef}
            isFavoritesEnabled
          />
        </div>
      )
    },
    [isResourceRestrained, items],
  )
  /**
   * Calculates row of tiles based on the columns count
   * each row will have a fixed number of columns where the tile index is calculated
   */
  const rowRenderer = useCallback(
    ({ index, style, isScrolling }) => {
      const rowIndex = index
      const itemCount = rowIndex * columnsCount - rowIndex
      return (
        <div key={index} style={{ top: style.top, height: style.height }} className={styles.row}>
          {Array.from({ length: columnsCount }, (_, columnIndex) =>
            tileRenderer({ index: rowIndex + columnIndex + itemCount, isScrolling }),
          )}
        </div>
      )
    },
    [columnsCount, tileRenderer],
  )

  // no items found messages
  if (!items.length) {
    return <VNNoItemsFound />
  }

  const memoizedRow = memo(rowRenderer, areEqual)

  return (
    <div className={classNames}>
      <AutoSizer>
        {({ height, width }) => (
          <List
            width={width}
            height={height}
            itemCount={itemsCount}
            itemSize={tileHeight}
            overscanCount={overscanCount}
            useIsScrolling={true}
            ref={listRef}
            style={{ paddingBottom: isMobile ? LIST_BOTTOM_PADDING_MOBILE : LIST_BOTTOM_PADDING, scrollbarWidth: 'none' }}
          >
            {memoizedRow}
          </List>
        )}
      </AutoSizer>
      <div className={cn(styles.bottomPadding, { [styles.hasCartItems]: hasCartItems })}></div>
    </div>
  )
}

Items.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onTapItem: PropTypes.func,
  className: PropTypes.string,
  searchQuery: PropTypes.string,
  highlightedItemsById: PropTypes.object,
}

export default memo(Items)
